import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { ArrowLeft, BookOpen, Save, RotateCcw, Printer, Plus } from 'lucide-react';
import { toast } from 'sonner';
import AIPracticeScenario from '../shared/AIPracticeScenario';
import PageMessaging from '../shared/PageMessaging';

export default function InterpersonalDance() {
  const navigate = useNavigate();
  const printPage = () => { window.print(); toast.success('Print dialog opened'); };
  
  const [entries, setEntries] = useState(() => {
    const saved = localStorage.getItem('interpersonalDanceEntries');
    return saved ? JSON.parse(saved) : [{ person: '', action: '', thought: '', felt: '', reacted: '' }];
  });

  const saveProgress = () => {
    localStorage.setItem('interpersonalDanceEntries', JSON.stringify(entries));
    toast.success('Saved!');
  };

  const reset = () => {
    if (window.confirm('Reset all entries?')) {
      setEntries([{ person: '', action: '', thought: '', felt: '', reacted: '' }]);
      localStorage.removeItem('interpersonalDanceEntries');
      toast.success('Reset!');
    }
  };

  const addEntry = () => {
    setEntries([...entries, { person: '', action: '', thought: '', felt: '', reacted: '' }]);
  };

  const updateEntry = (idx, field, value) => {
    const updated = [...entries];
    updated[idx][field] = value;
    setEntries(updated);
  };

  useEffect(() => {
    const interval = setInterval(() => localStorage.setItem('interpersonalDanceEntries', JSON.stringify(entries)), 30000);
    return () => clearInterval(interval);
  }, [entries]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <style>{`@media print { body { background: white !important; } .no-print { display: none !important; } .print-page { background: white !important; color: black !important; } }`}</style>
      
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50 no-print">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800">
                <ArrowLeft className="h-4 w-4 mr-2" />Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">The Interpersonal Dance of The 3 Areas</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={printPage} variant="outline" size="sm" className="border-blue-600 hover:bg-blue-900/20 text-white">
                <Printer className="h-4 w-4 mr-2" />Print
              </Button>
              <Button onClick={reset} variant="outline" size="sm" className="border-red-600 hover:bg-red-900/20 text-white">
                <RotateCcw className="h-4 w-4 mr-2" />Reset
              </Button>
              <Button onClick={saveProgress} variant="outline" size="sm" className="border-green-600 hover:bg-green-900/20 text-white">
                <Save className="h-4 w-4 mr-2" />Save
              </Button>
              <div className="flex items-center gap-2 text-slate-400 text-sm"><BookOpen className="h-4 w-4" /><span>Pages 49-50</span></div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12 print-page">
        <div className="max-w-5xl mx-auto">
          <div className="bg-purple-900/20 border border-purple-700/30 rounded-lg p-6 mb-8">
            <p className="text-purple-100 leading-relaxed mb-4">
              Each of us is constantly cycling through the three areas of attention as we go through our day. And, we are constantly interacting with other people as they cycle through their three areas of attention.
            </p>
            <p className="text-purple-200 font-semibold">
              This exercise helps us notice our thoughts, feelings, and reactions as we interact with others. The more aware we become of our patterns the more we will choose effective ways of relating.
            </p>
          </div>

          <div className="bg-amber-900/20 border border-amber-700/30 rounded-lg p-4 mb-8">
            <p className="text-amber-100 italic text-center">
              It is impossible to continue doing something that is self-defeating when we become aware of its negative effect in our life.
            </p>
          </div>

          {/* Example */}
          <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-6 mb-8">
            <h3 className="text-xl font-bold text-blue-200 mb-4">Example:</h3>
            <div className="space-y-2 text-blue-100">
              <p><span className="font-semibold text-white">Person A</span> (girlfriend) <span className="font-semibold">said or did:</span> Arrived a half hour late at the restaurant for our date.</p>
              <p><span className="font-semibold text-white">I thought:</span> "She doesn't really like me very much" and "She is so selfish."</p>
              <p><span className="font-semibold text-white">I felt:</span> Angry and sad.</p>
              <p><span className="font-semibold text-white">Then I reacted by:</span> Being withdrawn the rest of the night.</p>
            </div>
          </div>

          {/* Practice Entries */}
          <div className="space-y-6 mb-8">
            {entries.map((entry, idx) => (
              <div key={idx} className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-6">
                <h3 className="text-white font-semibold mb-4">Entry #{idx + 1}</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input value={entry.person} onChange={(e) => updateEntry(idx, 'person', e.target.value)} placeholder="Person A (name)" className="bg-slate-900/40 border-slate-700 text-white" />
                    <Input value={entry.action} onChange={(e) => updateEntry(idx, 'action', e.target.value)} placeholder="said or did (specific thing)" className="bg-slate-900/40 border-slate-700 text-white" />
                  </div>
                  <div>
                    <label className="text-purple-300 text-sm mb-2 block">I thought:</label>
                    <Textarea value={entry.thought} onChange={(e) => updateEntry(idx, 'thought', e.target.value)} placeholder="Thoughts I had in reaction to this event..." className="bg-purple-900/20 border-purple-700/30 text-white min-h-[60px]" />
                  </div>
                  <div>
                    <label className="text-red-300 text-sm mb-2 block">I felt:</label>
                    <Textarea value={entry.felt} onChange={(e) => updateEntry(idx, 'felt', e.target.value)} placeholder="The feelings I had in reaction to my thoughts..." className="bg-red-900/20 border-red-700/30 text-white min-h-[60px]" />
                  </div>
                  <div>
                    <label className="text-amber-300 text-sm mb-2 block">Then I reacted by:</label>
                    <Textarea value={entry.reacted} onChange={(e) => updateEntry(idx, 'reacted', e.target.value)} placeholder="How I responded in reaction to my thoughts and feelings..." className="bg-amber-900/20 border-amber-700/30 text-white min-h-[60px]" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Button onClick={addEntry} variant="outline" className="w-full border-green-600 text-green-200 hover:bg-green-900/20 mb-8">
            <Plus className="h-4 w-4 mr-2" />Add Another Entry
          </Button>

          <p className="text-center text-slate-400 text-sm mb-8">💾 Auto-saves every 30 seconds</p>
          <p className="text-center text-slate-500 text-xs mb-8">©Steve Torma • The REAL Center • www.therealcenter.org • 828-254-5613</p>

          <AIPracticeScenario pageTitle="The Interpersonal Dance" pageNumber={49} />
          <PageMessaging pageTitle="The Interpersonal Dance" pageNumber={49} />
        </div>
      </div>
    </div>
  );
}
